import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { maskPhone, UF_LIST } from '../../../../domain';
import { RestrictedApi } from '../restricted.api';
import { CompanyApi } from '../companies/companies.api';
import { StatusBandsPresenter } from '../../../../components/status-bands/status-bands.presenter';
import { MyDataKeys } from './my-data.key';
import { MyDataScope, MyDataTabScope, ProfileFieldScope } from './my-data.scope';
import { MY_DATA_TABS, MY_DATA_TAB_LABELS } from './my-data.types';
const LOG = Logger.get('Mormaii.MyDataPresenter');
// @Inject
const profileApi = RestrictedApi.INSTANCE;
// @Inject
const companyApi = CompanyApi.INSTANCE;
/**
 * Meus dados: perfil, senha e parametros de status.
 *
 * A terceira secao e o que o cliente pediu no item 1.3 do backlog - "estabelecer um
 * campo onde esses parametros possam ser preenchidos de forma individualizada" - e
 * que ja existe em producao desde 14/05/2026. Portado fiel, para a conversa com ele
 * ser sobre o que falta e nao sobre o que ja ha.
 *
 * Quem edita O QUE depende do papel: super admin mexe nos padroes que valem para
 * todos (`defaults`); os demais mexem nos proprios (`own`). A cadeia de
 * resolucao do backend e paciente -> tenant -> default.
 */
export class MyDataPresenter extends CubePresenter {
    constructor(app) {
        super(app, new MyDataScope());
        this.parentSlot = NOOP_VOID;
        this.tab = 'conta';
        /**
         * Quando a pessoa nao tem registro profissional.
         *
         * Ela se le por `/v2/accounts/me` e se salva por `PATCH /v2/accounts/me`,
         * que e o caminho que nao pede papel algum - `/v2/professionals/me` e
         * `PATCH /v2/professionals/me` exigem `ROLE_PROFESSIONAL`.
         */
        this.accountOnly = false;
        /** O editor de faixas - o mesmo que o detalhe do colaborador hospeda. */
        this.bandsPresenter = new StatusBandsPresenter(this.update, (count, warnings) => {
            const salvo = `${count} faixa${count === 1 ? '' : 's'} atualizada${count === 1 ? '' : 's'}.`;
            /*
             * O aviso de cobertura sobe de severidade e vai junto com o "salvo".
             *
             * Gravou, sim - e ficou um vao. Guardar isso so na tela, atras de um icone,
             * deixaria a pessoa sair daqui achando que terminou.
             */
            this.app.alert(warnings.length ? 'warning' : 'info', 'Parâmetros salvos', warnings.length ? `${salvo}\n\n${warnings.join('\n')}` : salvo);
        });
    }
    /**
     * Sem `last`: esta tela e folha, e quem escreve o cabecalho e o `showTab`.
     *
     * A troca de aba nao passa por aqui - ela move o proprio estado e chama
     * `updateHistory()` -, entao pendurar o `showSection` no `last` deixaria o
     * subtitulo falando da aba anterior.
     */
    async applyParameters(intent, initialization) {
        const keys = new MyDataKeys(this.app, intent);
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.owner = keys.owner;
        if (initialization) {
            this.scope.onSaveProfile = this.action(this.onSaveProfile);
            this.scope.onCurrentPasswordChanged = this.handlePassword.bind(this, 'currentPassword');
            this.scope.onNewPasswordChanged = this.handlePassword.bind(this, 'newPassword');
            this.scope.onConfirmPasswordChanged = this.handlePassword.bind(this, 'confirmPassword');
            this.scope.onSavePassword = this.action(this.onSavePassword);
            this.scope.onCancelPassword = this.action(this.onCancelPassword);
            this.scope.onHealthProfessionalChanged = this.handleHealthProfessionalChanged.bind(this);
            this.scope.bands = this.bandsPresenter.scope;
            this.scope.ufOptions = [...UF_LIST];
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        this.parentSlot(this.scope);
        if (initialization) {
            await this.load();
        }
        else if (this.isCompanyAdmin && this.owner?.companyInScope !== this.companyLoadedFor) {
            await this.reloadCompany();
        }
        // Depois do `load`: quais abas existem depende do papel, e o cabecalho diz o
        // alcance da aba aberta.
        this.showTab(keys.tab);
        return true;
    }
    publishParameters(intent) {
        const keys = new MyDataKeys(this.app, intent);
        keys.tab = this.tab;
    }
    showThemes() {
        this.scope.themes =
            this.tab === 'conta' && this.owner ? this.owner.themeOptions(this.update, () => this.showThemes()) : [];
    }
    /**
     * Troca de aba sem sair do place.
     *
     * `updateHistory()` e nao um flip: o place e o mesmo, e dar flip para onde ja se
     * esta seria recriar a tela inteira para mudar um parametro dela.
     */
    async onSelectTab(tab) {
        this.showTab(tab);
        this.updateHistory();
    }
    get isSuperAdmin() {
        return this.owner?.allows(['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']) ?? false;
    }
    get isCompanyAdmin() {
        return this.owner?.allows(['ROLE_COMPANY_ADMIN']) ?? false;
    }
    /**
     * Se vale a pena pedir o registro profissional.
     *
     * Pelo PAPEL, e nao tentando e vendo o que volta: `/v2/professionals/me` responde
     * 403 a quem nao o tem, e embora o servico traduza isso para `undefined`, e uma
     * ida ao servidor que ja se sabe perdida.
     */
    get isProfessional() {
        return this.owner?.allows(['ROLE_PROFESSIONAL']) ?? false;
    }
    async load() {
        this.scope.loading = true;
        this.scope.error = undefined;
        try {
            // As tres cargas em paralelo: nao dependem uma da outra.
            await Promise.all([this.loadAccount(), this.loadCompany(), this.loadBands()]);
        }
        catch (caught) {
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível carregar seus dados.';
        }
        finally {
            this.scope.loading = false;
        }
    }
    /**
     * A PESSOA logada - e agora ela e carregada para todo perfil.
     *
     * Antes, quem administrava uma empresa nunca chegava aqui: a tela montava o
     * formulario da empresa e voltava. O resultado era que o administrador de empresa
     * nao tinha como corrigir o proprio nome ou telefone, e "Meus Dados" mostrava os
     * dados de outra entidade.
     *
     * Dois caminhos, e a escolha e do PAPEL: `/v2/professionals/me` exige
     * `ROLE_PROFESSIONAL`; quem nao o tem se le por `/v2/accounts/me`, que nao pede
     * papel algum porque o alvo e sempre o da sessao.
     */
    async loadAccount() {
        this.professional = this.isProfessional ? await profileApi.fetchCurrentProfessional() : undefined;
        this.accountOnly = !this.professional;
        this.account = this.professional?.user ?? (await profileApi.fetchCurrentUser());
    }
    /**
     * A empresa, so para quem a administra - e a EM ESCOPO.
     *
     * Sem o parametro o servidor devolve a primeira empresa da pessoa, e quem
     * administra varias via os dados de uma sob os limites de outra; salvar gravava
     * na primeira (31/10/2026). `companyInScope` e indefinido so para o administrador
     * geral, que nao chega aqui.
     */
    async loadCompany() {
        const companyId = this.owner?.companyInScope;
        this.companyLoadedFor = companyId;
        this.company = this.isCompanyAdmin ? await profileApi.fetchCurrentCompany(companyId) : undefined;
    }
    /** A empresa do cabecalho mudou: o cadastro em maos e de outra. */
    async reloadCompany() {
        this.scope.error = undefined;
        try {
            await this.loadCompany();
        }
        catch (caught) {
            this.company = undefined;
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível carregar a empresa.';
        }
    }
    /**
     * As faixas da aba ABERTA - cada uma edita um degrau da cadeia.
     *
     * Trocar de aba recarrega: o editor guarda um alvo so, e mostrar os limites da
     * empresa sob o titulo de "Minha conta" seria pior do que a ida ao servidor.
     */
    async loadBands() {
        if (!this.tabHasBands) {
            return;
        }
        const alvo = this.bandTarget();
        this.scope.editingDefaults = alvo === 'defaults';
        await this.bandsPresenter.load(alvo);
    }
    /**
     * Se a aba aberta tem faixas para ESTE papel.
     *
     * As de "Minha conta" moram em `/v2/status-bands/own`, que o backend so
     * libera para `ROLE_PROFESSIONAL` e `ROLE_COMPANY_ADMIN`. Pedidas pelo
     * administrador da plataforma que nao e nenhum dos dois, voltam 403 - e 403 fora
     * de `roleAtStake` e sessao perdida: ele saia do painel ao abrir Configuracoes. As
     * faixas dele sao as do sistema, na aba ao lado.
     */
    get tabHasBands() {
        return this.tab !== 'conta' || this.isProfessional || this.isCompanyAdmin;
    }
    /** Que degrau esta aba edita. */
    bandTarget() {
        switch (this.tab) {
            case 'sistema':
                return 'defaults';
            case 'empresa':
                // Nulo quer dizer "a minha": quem administra uma so nao diz qual, e
                // quem administra varias tem a escolhida no cabecalho da casca.
                return { companyId: this.owner?.companyInScope ?? null };
            default:
                return 'own';
        }
    }
    /**
     * Quais abas existem, e o que a aberta mostra.
     *
     * Aqui mora a regra inteira da tela: o papel decide o CONJUNTO de abas, e a aba
     * aberta decide o formulario, se a senha aparece e de quem sao os limites.
     */
    showTab(wanted) {
        const disponiveis = MY_DATA_TABS.filter((tab) => this.allowsTab(tab));
        // Endereco pedindo aba que este papel nao tem - link antigo, ou papel que
        // mudou. Cai na primeira, em vez de abrir uma tela vazia.
        this.tab = disponiveis.includes(wanted) ? wanted : 'conta';
        this.scope.showTabs = disponiveis.length > 1;
        this.scope.tabs = disponiveis.map((tab) => this.tabOf(tab));
        this.scope.tab = this.tab;
        // A senha e da CONTA: nao existe senha de empresa nem do sistema. A aparencia
        // tambem.
        this.scope.showPassword = this.tab === 'conta';
        this.showThemes();
        /*
         * Cada aba edita o SEU degrau da cadeia - e sao tres.
         *
         * "Minha conta" os limites da pessoa, "Minha empresa" os da empresa e
         * "Sistema" os da plataforma. Ate 17/09/2026 o degrau do meio nao existia: os
         * limites eram por usuario, e "Minha empresa" nao os mostrava justamente
         * porque nao eram da empresa. Agora sao.
         */
        this.scope.showBands = this.tabHasBands;
        void this.loadBands();
        this.showProfile();
        this.owner?.showSection('my-data', 'Configurações', MY_DATA_TAB_LABELS[this.tab].hint);
    }
    /**
     * Quem ve cada aba.
     *
     * "Minha conta" todo mundo tem - e a unica que nao depende de papel nenhum.
     */
    allowsTab(tab) {
        switch (tab) {
            case 'conta':
                return true;
            case 'empresa':
                return this.isCompanyAdmin && this.company !== undefined;
            case 'sistema':
                return this.isSuperAdmin;
        }
    }
    tabOf(tab) {
        const scope = new MyDataTabScope();
        scope.identity = tab;
        scope.key = tab;
        scope.label = MY_DATA_TAB_LABELS[tab].label;
        scope.active = tab === this.tab;
        scope.onSelect = this.action(this.onSelectTab.bind(this, tab));
        scope.update = this.update;
        return scope;
    }
    /** O formulario da aba aberta. "Sistema" nao tem formulario - so os limites. */
    showProfile() {
        this.scope.profileDirty = false;
        this.scope.profileError = undefined;
        if (this.tab === 'empresa' && this.company) {
            this.scope.profileTitle = 'Dados da empresa';
            this.showCompanyFields(this.company);
            return;
        }
        if (this.tab === 'conta' && this.account) {
            this.scope.profileTitle = 'Seus dados';
            this.showAccountFields(this.account);
            return;
        }
        this.scope.profileTitle = 'Dados básicos';
        this.scope.profileFields = [];
        this.scope.showHealthProfessional = false;
    }
    /** Os campos da EMPRESA - a aba "Minha empresa". */
    showCompanyFields(company) {
        this.scope.formRevision += 1;
        this.scope.showHealthProfessional = false;
        this.scope.healthProfessional = false;
        /*
         * CNPJ, razao social e e-mail sao `readOnly`, e agora a view honra isso.
         *
         * Nao e enfeite: o servidor RECUSA a troca de CNPJ por quem nao e
         * administrador geral ("Somente o administrador pode alterar o CNPJ de uma
         * empresa"), que e o que a lei espera. Enquanto a view ignorava o
         * sinalizador, os tres apareciam editaveis e o salvar dizia "editado com
         * sucesso" sem ter enviado campo nenhum.
         */
        this.scope.profileFields = [
            this.buildField({ name: 'cnpj', label: 'CNPJ', placeholder: 'CNPJ', value: company.cnpj, readOnly: true }),
            this.buildField({
                name: 'razaoSocial',
                label: 'RAZÃO SOCIAL',
                placeholder: 'Razão Social',
                value: company.razaoSocial,
                readOnly: true
            }),
            this.buildField({
                name: 'nomeFantasia',
                label: 'NOME FANTASIA*',
                placeholder: 'Nome Fantasia',
                value: company.nomeFantasia ?? '',
                span: 'col-span-2 2xl:col-span-3'
            }),
            this.buildField({
                name: 'ramoAtividade',
                label: 'RAMO DE ATIVIDADE*',
                placeholder: 'Ramo de Atividade',
                value: company.ramoAtividade ?? '',
                span: 'col-span-2 2xl:col-span-3'
            }),
            this.buildField({
                name: 'contactName',
                label: 'NOME RESPONSÁVEL*',
                placeholder: 'Nome do Responsável',
                value: company.contactName ?? '',
                span: 'col-span-2 2xl:col-span-3'
            }),
            // O e-mail e a chave de contato: nao se edita aqui.
            this.buildField({
                name: 'contactEmail',
                label: 'E-MAIL',
                placeholder: 'E-mail',
                value: company.contactEmail ?? '',
                readOnly: true,
                disabled: true
            }),
            this.buildField({
                name: 'contactPhone',
                label: 'TELEFONE*',
                placeholder: '(DDD) 00000-0000',
                value: maskPhone(company.contactPhone ?? '')
            })
        ];
    }
    /** Os campos da PESSOA - a aba "Minha conta", que todo perfil tem. */
    showAccountFields(user) {
        this.scope.formRevision += 1;
        // Profissional de saude e quem TEM registro de conselho - e como a
        // referencia decide (`!!data?.registrationNumber`).
        this.scope.healthProfessional = Boolean(this.professional?.registrationNumber);
        // O radio nao aparece para admin: la ele e escondido para
        // `ROLE_ADMIN`/`ROLE_SUPER_ADMIN`. Nem para quem nao tem registro
        // profissional, que nao teria onde salvar o que o radio liga.
        this.scope.showHealthProfessional = !this.isSuperAdmin && !this.accountOnly;
        this.scope.profileFields = [
            this.buildField({ name: 'firstName', label: 'NOME*', placeholder: 'Nome', value: user.firstName ?? '' }),
            this.buildField({
                name: 'lastName',
                label: 'SOBRENOME*',
                placeholder: 'Sobrenome',
                value: user.lastName ?? '',
                span: 'col-span-2 2xl:col-span-3'
            }),
            this.buildField({
                name: 'socialName',
                label: 'NOME SOCIAL',
                placeholder: 'Nome Social',
                value: user.socialName ?? '',
                span: 'col-span-2 2xl:col-span-3'
            }),
            this.buildField({
                name: 'phone',
                label: 'TELEFONE*',
                placeholder: '(DDD) 00000-0000',
                value: maskPhone(user.telephoneNumber ?? '')
            }),
            // CPF e e-mail nao se editam: um e imutavel, o outro e a chave de login.
            this.buildField({ name: 'cpf', label: 'CPF', placeholder: 'CPF', value: user.cpf ?? '', readOnly: true }),
            this.buildField({
                name: 'email',
                label: 'EMAIL',
                placeholder: 'E-mail',
                value: user.email ?? '',
                readOnly: true,
                disabled: true,
                dimmed: true
            })
        ];
        if (this.scope.healthProfessional) {
            this.showRegistrationFields();
        }
    }
    /**
     * Os dois campos do registro de conselho, que so existem para profissional.
     *
     * Entram e saem da lista conforme o radio, como o `isProfessional &&` de la.
     */
    showRegistrationFields() {
        this.scope.profileFields = [
            ...this.scope.profileFields.filter((field) => field.identity !== 'registrationNumber' && field.identity !== 'uf'),
            this.buildField({
                name: 'registrationNumber',
                label: 'Nº REGISTRO CONSELHO*',
                placeholder: 'Nº Registro Conselho',
                value: this.professional?.registrationNumber ?? ''
            }),
            this.buildField({
                name: 'uf',
                label: 'UF*',
                placeholder: 'UF',
                value: this.professional?.UF ?? '',
                kind: 'uf',
                maxLength: 2
            })
        ];
    }
    hideRegistrationFields() {
        this.scope.profileFields = this.scope.profileFields.filter((field) => field.identity !== 'registrationNumber' && field.identity !== 'uf');
    }
    buildField(spec) {
        const scope = new ProfileFieldScope();
        scope.identity = spec.name;
        scope.label = spec.label;
        scope.placeholder = spec.placeholder;
        scope.value = spec.value;
        scope.span = spec.span ?? 'col-span-2';
        scope.kind = spec.kind ?? 'text';
        scope.readOnly = spec.readOnly ?? false;
        scope.disabled = spec.disabled ?? false;
        scope.dimmed = spec.dimmed ?? false;
        scope.maxLength = spec.maxLength;
        scope.onChanged = (next) => {
            scope.value = spec.name === 'phone' || spec.name === 'contactPhone' ? maskPhone(next) : next;
            // Qualquer tecla suja o formulario, e e o que liga o botao - o `isDirty`
            // do react-hook-form faz o mesmo.
            this.scope.profileDirty = true;
        };
        scope.update = this.update;
        return scope;
    }
    valueOf(name) {
        return this.scope.profileFields.find((field) => field.identity === name)?.value ?? '';
    }
    // ========== ACOES ==========
    /**
     * Salvar PERGUNTA antes, como la: um dialogo de confirmacao, e outro de sucesso.
     *
     * Os dois sao o `DialogAlert` da referencia, que aqui e o dialogo compartilhado
     * do shell - mesma marcacao, mesmos textos, mesmo icone.
     */
    async onSaveProfile() {
        const daEmpresa = this.tab === 'empresa';
        this.app.confirm('Confirmar Edição de Dados', daEmpresa
            ? 'Continuar e confirmar a edição dos dados da empresa?'
            : 'Continuar e confirmar a edição dos dados básicos de sua conta?', 'Confirmar', async () => {
            await this.action(this.postProfile)();
        }, { cancelText: 'Cancelar', icon: 'account-edit' });
    }
    /**
     * Salva o que a aba ABERTA edita.
     *
     * O despacho e pela aba, e nao pelo papel: um administrador de empresa agora tem
     * as duas, e decidir pelo papel mandaria os dados pessoais dele para o cadastro
     * da empresa.
     *
     * Os campos `readOnly` nao viajam - CNPJ e razao social vao do valor CARREGADO, e
     * nao do digitado. O servidor recusaria a troca de CNPJ de qualquer forma; mandar
     * o digitado so trocaria um "nada aconteceu" silencioso por um 403.
     */
    async postProfile() {
        this.scope.profileBusy = true;
        this.scope.profileError = undefined;
        try {
            if (this.tab === 'empresa' && this.company) {
                await companyApi.updateCompany(this.company.id, {
                    razaoSocial: this.company.razaoSocial,
                    nomeFantasia: this.valueOf('nomeFantasia'),
                    ramoAtividade: this.valueOf('ramoAtividade'),
                    contactName: this.valueOf('contactName'),
                    contactEmail: this.company.contactEmail,
                    contactPhone: this.valueOf('contactPhone'),
                    active: this.company.active
                });
            }
            else if (this.accountOnly) {
                await profileApi.updateCurrentUser({
                    firstName: this.valueOf('firstName'),
                    lastName: this.valueOf('lastName'),
                    socialName: this.valueOf('socialName') || null,
                    telephoneNumber: this.valueOf('phone')
                });
            }
            else if (this.professional) {
                await profileApi.updateProfessional({
                    registrationNumber: this.valueOf('registrationNumber') || null,
                    UF: this.valueOf('uf') || null,
                    professionalTypeId: this.professional.professionalTypeId ?? 0,
                    user: {
                        firstName: this.valueOf('firstName'),
                        lastName: this.valueOf('lastName'),
                        email: this.professional.user.email,
                        socialName: this.valueOf('socialName') || null,
                        telephoneNumber: this.valueOf('phone')
                    }
                });
            }
            this.scope.profileDirty = false;
            this.app.notify(this.tab === 'empresa' ? 'Dados da Empresa Editados' : 'Dados Básicos Editados', this.tab === 'empresa'
                ? 'Os dados da empresa foram editados com sucesso!'
                : 'Os dados básicos de sua conta foram editados com sucesso!', 'Continuar', 'account-edit');
            // O que esta em maos acompanha o que foi salvo: sem isto, trocar de aba
            // e voltar traria de volta os valores anteriores.
            await this.reloadSaved();
        }
        catch (caught) {
            this.scope.profileError = caught instanceof Error ? caught.message : 'Não foi possível salvar.';
        }
        finally {
            this.scope.profileBusy = false;
        }
    }
    async reloadSaved() {
        if (this.tab === 'empresa') {
            await this.loadCompany();
        }
        else {
            await this.loadAccount();
        }
    }
    handlePassword(field, value) {
        this.scope[field] = value;
        this.scope.passwordDirty = true;
    }
    handleHealthProfessionalChanged(value) {
        this.scope.healthProfessional = value;
        this.scope.profileDirty = true;
        if (value) {
            this.showRegistrationFields();
        }
        else {
            this.hideRegistrationFields();
        }
    }
    /** O "Cancelar" da senha: limpa os tres campos, como o `reset()` de la. */
    async onCancelPassword() {
        this.scope.currentPassword = '';
        this.scope.newPassword = '';
        this.scope.confirmPassword = '';
        this.scope.passwordError = undefined;
        this.scope.passwordDirty = false;
        this.scope.formRevision += 1;
    }
    async onSavePassword() {
        const current = this.scope.currentPassword;
        const next = this.scope.newPassword;
        if (!current || !next) {
            this.scope.passwordError = 'Informe a senha atual e a nova.';
            return;
        }
        if (next !== this.scope.confirmPassword) {
            this.scope.passwordError = 'A confirmação não confere com a nova senha.';
            return;
        }
        this.app.confirm('Alterar Senha', 'Continuar e confirmar a alteração de sua senha atual para a nova senha informada?', 'Confirmar Nova Senha', async () => {
            await this.action(this.postPassword)();
        }, { cancelText: 'Cancelar', icon: 'account-edit' });
    }
    async postPassword() {
        const current = this.scope.currentPassword;
        const next = this.scope.newPassword;
        this.scope.passwordBusy = true;
        this.scope.passwordError = undefined;
        try {
            await profileApi.changePassword(current, next);
            this.app.notify('Senha Alterada', 'Nova senha alterada com sucesso!', 'Continuar', 'account-edit');
            this.scope.currentPassword = '';
            this.scope.newPassword = '';
            this.scope.confirmPassword = '';
            this.scope.passwordDirty = false;
            this.scope.formRevision += 1;
        }
        catch (caught) {
            this.scope.passwordError = caught instanceof Error ? caught.message : 'Não foi possível trocar a senha.';
        }
        finally {
            this.scope.passwordBusy = false;
        }
    }
}
//# sourceMappingURL=my-data.presenter.js.map